/**
 * API service for Microsoft integration (Mail + Calendar)
 */

import { apiGet, apiPost, apiDelete } from '../../api/axiosInstance';
import { getMainDomainOrigin } from '../../utils/platform';
import { publicAxios } from '../../utils/oauthCrossDomain';
import type { MicrosoftOAuthOriginResolveResponse } from '../../../generated/api/models/microsoftOAuthOriginResolveResponse';

const API_BASE = '/api/v1/integrations/microsoft';

export interface MicrosoftIntegrationStatus {
  connected: boolean;
  credentials_configured: boolean;
  mail_enabled: boolean;
  calendar_enabled: boolean;
  onedrive_enabled: boolean;
  connected_email?: string;
  connection_error?: string;
}

export interface MicrosoftMailMessage {
  id: string;
  subject: string;
  from: string;
  to: string;
  date: string;
  snippet: string;
  body?: string;
  is_read: boolean;
}

export interface MicrosoftMailListResponse {
  messages: MicrosoftMailMessage[];
  next_page_token?: string;
  total_count?: number;
}

export interface OneDriveFile {
  id: string;
  name: string;
  mime_type: string;
  is_folder: boolean;
  size?: number;
  created_time?: string;
  modified_time?: string;
  web_view_link?: string;
  download_link?: string;
  icon_link?: string;
  parent_ids?: string[];
}

export interface OneDriveListResponse {
  files: OneDriveFile[];
  next_page_token?: string;
}

export const microsoftIntegrationApi = {
  getStatus: async (): Promise<{ data: MicrosoftIntegrationStatus }> => {
    return apiGet(`${API_BASE}/status`);
  },

  initiateAuth: async (scopes?: string[]): Promise<{ data: { auth_url: string } }> => {
    const redirectUri = `${getMainDomainOrigin()}/oauth/callback/microsoft`;
    return apiPost(`${API_BASE}/auth`, { scopes, redirect_uri: redirectUri });
  },

  // Cross-domain OAuth: the canonical-host callback page asks which domain the
  // admin STARTED the connect on so it can bounce back there to complete under
  // their tenant session. Unauthenticated (keyed by the one-time OAuth state);
  // a forged/expired state resolves to null (no bounce). See MicrosoftOAuthCallbackPage.
  resolveOAuthOrigin: async (state: string): Promise<string | null> => {
    const res = await publicAxios.post<MicrosoftOAuthOriginResolveResponse>(
      `${API_BASE}/oauth/resolve-origin`,
      { state },
    );
    return res.data.origin_domain ?? null;
  },

  // Complete OAuth: exchange the authorization code (authenticated, on the domain
  // that owns the admin's session). The backend verifies the one-time state binds
  // to this session (anti-CSRF) and reuses the canonical redirect_uri it recorded
  // at /auth time — so only code + state are sent.
  completeAuth: async (code: string, state: string): Promise<{ data: unknown }> => {
    return apiPost(`${API_BASE}/callback`, { code, state });
  },

  disconnect: async (): Promise<void> => {
    return apiDelete(`${API_BASE}/disconnect`);
  },

  mail: {
    list: async (params?: { query?: string; max_results?: number; page_token?: string }): Promise<{ data: MicrosoftMailListResponse }> => {
      const searchParams = new URLSearchParams();
      if (params?.query) searchParams.set('q', params.query);
      if (params?.max_results) searchParams.set('max_results', String(params.max_results));
      if (params?.page_token) searchParams.set('page_token', params.page_token);
      const qs = searchParams.toString();
      return apiGet(`${API_BASE}/mail/messages${qs ? `?${qs}` : ''}`);
    },

    get: async (messageId: string): Promise<{ data: MicrosoftMailMessage }> => {
      return apiGet(`${API_BASE}/mail/messages/${messageId}`);
    },
  },

  onedrive: {
    list: async (params?: { folder_id?: string; query?: string; max_results?: number; page_token?: string }): Promise<{ data: OneDriveListResponse }> => {
      const searchParams = new URLSearchParams();
      if (params?.folder_id) searchParams.set('folder_id', params.folder_id);
      if (params?.query) searchParams.set('q', params.query);
      if (params?.max_results) searchParams.set('max_results', String(params.max_results));
      if (params?.page_token) searchParams.set('page_token', params.page_token);
      const qs = searchParams.toString();
      return apiGet(`${API_BASE}/onedrive/files${qs ? `?${qs}` : ''}`);
    },

    get: async (fileId: string): Promise<{ data: OneDriveFile }> => {
      return apiGet(`${API_BASE}/onedrive/files/${fileId}`);
    },
  },
};
